import { useState, useEffect } from 'react';
import { ArrowRight, Target, Heart, Shield, Zap, Users, Globe, Clock, TrendingUp, Star, Award, Building, School } from 'lucide-react';

const VisionMissionPage = () => {
  const [activeTimeline, setActiveTimeline] = useState(0);
  
  // Auto-advance timeline animation
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTimeline((prev) => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-pure-white via-pure-white to-electric-blue/10 text-midnight-blue overflow-hidden">
      
      {/* Hero Section - Why Statement */}
      <section className="px-4 py-16 md:py-24 relative">
        {/* Background Effects */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-electric-blue/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-burnt-orange/15 rounded-full blur-3xl animate-pulse delay-2000"></div>
        </div>

        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-16">
            {/* Mission Badge */}
            <div className="flex justify-center mb-8">
              <div className="glass-primary p-6 rounded-3xl inline-flex items-center gap-4 animate-neural-pulse">
                <div className="w-16 h-16 bg-gradient-to-br from-electric-blue to-burnt-orange rounded-full flex items-center justify-center">
                  <Heart className="w-8 h-8 text-white" />
                </div>
                <div className="text-left">
                  <div className="text-sm font-medium text-electric-blue uppercase tracking-wide">
                    Sứ Mệnh Của Chúng Tôi
                  </div>
                  <div className="text-2xl font-bold text-midnight-blue">
                    An Tâm Cho Mọi Gia Đình
                  </div>
                </div>
              </div>
            </div>

            {/* Main Why Statement */}
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-8 text-midnight-blue leading-tight">
              Mọi Gia Đình Xứng Đáng
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-electric-blue to-burnt-orange relative">
                Có Sự An Tâm Mỗi Ngày
                <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-electric-blue to-burnt-orange rounded-full"></div>
              </span>
            </h1>

            <div className="max-w-5xl mx-auto mb-12">
              <p className="text-xl md:text-2xl text-midnight-blue/80 leading-relaxed mb-8">
                Chúng tôi tin rằng mọi gia đình xứng đáng có <strong className="text-electric-blue">sự an tâm rõ ràng mỗi ngày</strong>, 
                và được <strong className="text-burnt-orange">trợ giúp thực sự trong vài chục giây</strong> khi cần.
              </p>
              
              <div className="glass-primary p-8 rounded-3xl text-left max-w-4xl mx-auto">
                <p className="text-lg text-midnight-blue/80 leading-relaxed">
                  Sự gia tăng của lối sống chung cư cao tầng, sự trưởng thành của e‑ID quốc gia và khả năng cảm biến 
                  chỉ bằng điện thoại khiến việc biến các tòa nhà "vô danh" thành những <em className="text-electric-blue font-semibold">lưới an toàn 
                  mang tính cộng đồng, đáng tin cậy</em> trở nên khả thi—<strong className="text-burnt-orange">mà không phải đánh đổi quyền riêng tư hay phẩm giá</strong>.
                </p>
              </div>
            </div>

            {/* Key Promise */}
            <div className="glass-secondary p-8 rounded-3xl max-w-4xl mx-auto mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-midnight-blue mb-6">
                Lời Hứa Đơn Giản Mỗi Ngày
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-electric-blue/20 rounded-2xl flex items-center justify-center">
                    <Users className="w-8 h-8 text-electric-blue" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-bold text-midnight-blue">Biết Họ Ở Đâu</h3>
                    <p className="text-midnight-blue/70">An tâm về vị trí</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-burnt-orange/20 rounded-2xl flex items-center justify-center">
                    <Shield className="w-8 h-8 text-burnt-orange" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-bold text-midnight-blue">Họ Có Ổn Không</h3>
                    <p className="text-midnight-blue/70">Theo dõi an toàn</p>
                  </div>
                </div>
              </div>
              <div className="mt-8 p-6 bg-gradient-to-r from-electric-blue/10 to-burnt-orange/10 rounded-2xl">
                <div className="flex items-center justify-center gap-3">
                  <Zap className="w-6 h-6 text-electric-blue" />
                  <span className="text-lg font-semibold text-midnight-blue">
                    Khi cần, sự hỗ trợ đang tới trong <span className="text-burnt-orange">30 giây</span>
                  </span>
                  <Zap className="w-6 h-6 text-burnt-orange" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision Timeline Section */}
      <section className="px-4 py-16 bg-gradient-to-r from-electric-blue/5 to-burnt-orange/5">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-midnight-blue">
              Tầm Nhìn Tương Lai
            </h2>
            <p className="text-lg text-midnight-blue/80 max-w-3xl mx-auto">
              Từ lưới phản ứng 30 giây đến tiêu chuẩn khu vực - hành trình biến đổi châu Á
            </p>
          </div>

          {/* Interactive Timeline */}
          <div className="relative max-w-5xl mx-auto">
            {/* Timeline Navigation */}
            <div className="flex justify-center mb-12 gap-4">
              {[
                { year: "Hiện Tại", label: "2025" },
                { year: "10 Năm", label: "2035" },
                { year: "20 Năm", label: "2045" }
              ].map((period, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTimeline(index)}
                  className={`px-6 py-3 rounded-2xl font-semibold transition-all duration-300 ${
                    activeTimeline === index
                      ? 'bg-electric-blue text-white shadow-lg'
                      : 'bg-white/50 text-midnight-blue hover:bg-white/80'
                  }`}
                >
                  <div className="text-sm">{period.year}</div>
                  <div className="text-lg font-bold">{period.label}</div>
                </button>
              ))}
            </div>

            {/* Timeline Content */}
            <div className="min-h-[400px]">
              {/* Current State - 2025 */}
              {activeTimeline === 0 && (
                <div className="glass-primary p-8 rounded-3xl animate-fade-in">
                  <div className="text-center mb-8">
                    <div className="w-20 h-20 bg-gradient-to-br from-electric-blue to-burnt-orange rounded-full flex items-center justify-center mx-auto mb-4">
                      <Target className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-midnight-blue mb-4">Khởi Đầu Hành Trình</h3>
                    <p className="text-midnight-blue/80">
                      Xây dựng nền tảng niềm tin tại Việt Nam với 100 gia đình tiên phong
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center p-6 bg-white/30 rounded-2xl">
                      <Building className="w-8 h-8 text-electric-blue mx-auto mb-3" />
                      <h4 className="font-bold text-midnight-blue mb-2">Chung Cư Cao Tầng</h4>
                      <p className="text-sm text-midnight-blue/70">Triển khai tại TP.HCM & Hà Nội</p>
                    </div>
                    <div className="text-center p-6 bg-white/30 rounded-2xl">
                      <School className="w-8 h-8 text-burnt-orange mx-auto mb-3" />
                      <h4 className="font-bold text-midnight-blue mb-2">Mạng Lưới Trường Học</h4>
                      <p className="text-sm text-midnight-blue/70">Kết nối phụ huynh & giáo viên</p>
                    </div>
                    <div className="text-center p-6 bg-white/30 rounded-2xl">
                      <Shield className="w-8 h-8 text-electric-blue mx-auto mb-3" />
                      <h4 className="font-bold text-midnight-blue mb-2">Quyền Riêng Tư</h4>
                      <p className="text-sm text-midnight-blue/70">Đồng thuận & eKYC Việt Nam</p>
                    </div>
                  </div>
                </div>
              )}

              {/* 10 Year Vision - 2035 */}
              {activeTimeline === 1 && (
                <div className="glass-primary p-8 rounded-3xl animate-fade-in">
                  <div className="text-center mb-8">
                    <div className="w-20 h-20 bg-gradient-to-br from-electric-blue to-burnt-orange rounded-full flex items-center justify-center mx-auto mb-4">
                      <Globe className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-midnight-blue mb-4">Lớp An Toàn Châu Á</h3>
                    <p className="text-midnight-blue/80">
                      100 thành phố trọng điểm với lưới phản ứng 30 giây trên toàn khu vực
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="text-xl font-bold text-midnight-blue mb-4 flex items-center gap-2">
                        <Clock className="w-6 h-6 text-electric-blue" />
                        Thời Gian Phản Ứng
                      </h4>
                      <ul className="space-y-3 text-midnight-blue/80">
                        <li className="flex items-center gap-3">
                          <div className="w-2 h-2 bg-electric-blue rounded-full"></div>
                          Hàng xóm đến cửa: <strong>&lt; 3 phút</strong>
                        </li>
                        <li className="flex items-center gap-3">
                          <div className="w-2 h-2 bg-burnt-orange rounded-full"></div>
                          Xác nhận SOS: <strong>&lt; 30 giây</strong>
                        </li>
                        <li className="flex items-center gap-3">
                          <div className="w-2 h-2 bg-electric-blue rounded-full"></div>
                          Thâm nhập tòa nhà: <strong>≥ 30%</strong>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-midnight-blue mb-4 flex items-center gap-2">
                        <Award className="w-6 h-6 text-burnt-orange" />
                        Tiêu Chuẩn Khu Vực
                      </h4>
                      <ul className="space-y-3 text-midnight-blue/80">
                        <li className="flex items-center gap-3">
                          <div className="w-2 h-2 bg-burnt-orange rounded-full"></div>
                          Chia sẻ dựa trên đồng thuận
                        </li>
                        <li className="flex items-center gap-3">
                          <div className="w-2 h-2 bg-electric-blue rounded-full"></div>
                          Kiểm soát tôn trọng teen
                        </li>
                        <li className="flex items-center gap-3">
                          <div className="w-2 h-2 bg-burnt-orange rounded-full"></div>
                          Lưu trữ dữ liệu trong nước
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* 20 Year Vision - 2045 */}
              {activeTimeline === 2 && (
                <div className="glass-primary p-8 rounded-3xl animate-fade-in">
                  <div className="text-center mb-8">
                    <div className="w-20 h-20 bg-gradient-to-br from-electric-blue to-burnt-orange rounded-full flex items-center justify-center mx-auto mb-4">
                      <TrendingUp className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-midnight-blue mb-4">Kết Cấu Đô Thị Kiên Cường</h3>
                    <p className="text-midnight-blue/80">
                      Can thiệp của người dân trở thành phản xạ văn hóa - thành phố tự chữa lành
                    </p>
                  </div>
                  <div className="space-y-6">
                    <div className="flex items-center gap-6 p-6 bg-gradient-to-r from-electric-blue/10 to-transparent rounded-2xl">
                      <div className="w-16 h-16 bg-electric-blue/20 rounded-2xl flex items-center justify-center">
                        <Clock className="w-8 h-8 text-electric-blue" />
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-midnight-blue">Thời Gian Phản Ứng Toàn Thành Phố</h4>
                        <p className="text-midnight-blue/80">Giảm xuống dưới <strong className="text-electric-blue">4 phút</strong></p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6 p-6 bg-gradient-to-r from-burnt-orange/10 to-transparent rounded-2xl">
                      <div className="w-16 h-16 bg-burnt-orange/20 rounded-2xl flex items-center justify-center">
                        <Heart className="w-8 h-8 text-burnt-orange" />
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-midnight-blue">Giao Thức Vòng Tròn Tin Cậy</h4>
                        <p className="text-midnight-blue/80">Chuẩn mở được <strong className="text-burnt-orange">thành phố, tòa nhà, trường học</strong> áp dụng</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6 p-6 bg-gradient-to-r from-electric-blue/10 to-transparent rounded-2xl">
                      <div className="w-16 h-16 bg-electric-blue/20 rounded-2xl flex items-center justify-center">
                        <Zap className="w-8 h-8 text-electric-blue" />
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-midnight-blue">AI An Sinh Gia Đình</h4>
                        <p className="text-midnight-blue/80">Vận hành mặc định từ điện thoại, <strong className="text-electric-blue">AI diễn giải đời thường</strong></p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Strategic Pillars */}
      <section className="px-4 py-16">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-midnight-blue">
              5 Trụ Cột Chiến Lược
            </h2>
            <p className="text-lg text-midnight-blue/80 max-w-3xl mx-auto">
              Nền tảng vững chắc để xây dựng tương lai an toàn cho gia đình châu Á
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Pillar 1 */}
            <div className="glass-primary p-6 rounded-2xl hover:glass-secondary transition-all duration-300 group">
              <div className="w-16 h-16 bg-gradient-to-br from-electric-blue to-electric-blue/80 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-midnight-blue">Hạ Tầng Niềm Tin</h3>
              <p className="text-midnight-blue/80 mb-4 leading-relaxed">
                Xác thực eKYC, quyền theo vai trò và phòng chống gian lận là các nguyên thủy cốt lõi.
              </p>
              <div className="text-sm text-electric-blue font-medium bg-electric-blue/10 px-3 py-1 rounded-full inline-block">
                Bảo mật tuyệt đối
              </div>
            </div>

            {/* Pillar 2 */}
            <div className="glass-primary p-6 rounded-2xl hover:glass-secondary transition-all duration-300 group">
              <div className="w-16 h-16 bg-gradient-to-br from-burnt-orange to-burnt-orange/80 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-midnight-blue">Vòng Thói Quen Hằng Ngày</h3>
              <p className="text-midnight-blue/80 mb-4 leading-relaxed">
                Biên nhận đến nơi, geofence lộ trình an toàn tạo giá trị ngoài tình huống khẩn cấp.
              </p>
              <div className="text-sm text-burnt-orange font-medium bg-burnt-orange/10 px-3 py-1 rounded-full inline-block">
                Bền dính mỗi ngày
              </div>
            </div>

            {/* Pillar 3 */}
            <div className="glass-primary p-6 rounded-2xl hover:glass-secondary transition-all duration-300 group">
              <div className="w-16 h-16 bg-gradient-to-br from-electric-blue/80 to-burnt-orange rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-midnight-blue">Quyền Riêng Tư Ưu Tiên</h3>
              <p className="text-midnight-blue/80 mb-4 leading-relaxed">
                Chia sẻ dựa trên đồng thuận, lưu trữ nội địa và trí tuệ trên thiết bị.
              </p>
              <div className="text-sm text-electric-blue font-medium bg-electric-blue/10 px-3 py-1 rounded-full inline-block">
                Đồng thuận 100%
              </div>
            </div>

            {/* Pillar 4 */}
            <div className="glass-primary p-6 rounded-2xl hover:glass-secondary transition-all duration-300 group">
              <div className="w-16 h-16 bg-gradient-to-br from-burnt-orange/80 to-electric-blue rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Building className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-midnight-blue">Phân Phối Cộng Đồng</h3>
              <p className="text-midnight-blue/80 mb-4 leading-relaxed">
                Trường học và chung cư là kênh chính; cộng đồng mẹ và telco/fintech khuếch đại.
              </p>
              <div className="text-sm text-burnt-orange font-medium bg-burnt-orange/10 px-3 py-1 rounded-full inline-block">
                Lan tỏa tự nhiên
              </div>
            </div>

            {/* Pillar 5 */}
            <div className="glass-primary p-6 rounded-2xl hover:glass-secondary transition-all duration-300 group md:col-span-2 lg:col-span-1">
              <div className="w-16 h-16 bg-gradient-to-br from-electric-blue to-burnt-orange/80 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-midnight-blue">Tác Động Đo Lường</h3>
              <p className="text-midnight-blue/80 mb-4 leading-relaxed">
                Báo cáo "phút rút ngắn / mạng sống cứu được"; đồng xây KPI với EMS, trường học.
              </p>
              <div className="text-sm text-electric-blue font-medium bg-electric-blue/10 px-3 py-1 rounded-full inline-block">
                Kết quả minh bạch
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* North Star Metrics */}
      <section className="px-4 py-16 bg-gradient-to-r from-electric-blue/5 to-burnt-orange/5">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-midnight-blue">
              Chỉ Số Dẫn Đường
            </h2>
            <p className="text-lg text-midnight-blue/80 max-w-3xl mx-auto">
              Những con số quan trọng nhất để đo lường thành công và tác động thực tế
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="glass-dark p-8 rounded-3xl text-white">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-electric-blue/30 rounded-2xl flex items-center justify-center">
                  <Users className="w-8 h-8 text-electric-blue" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">≥ 60%</h3>
                  <p className="text-white/80">Giữ chân gia đình sau 12 tháng</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-white/80">Thâm nhập tòa nhà</span>
                  <span className="font-bold text-electric-blue">≥ 30%</span>
                </div>
                <div className="w-full bg-white/20 rounded-full h-2">
                  <div className="bg-electric-blue h-2 rounded-full w-3/4"></div>
                </div>
              </div>
            </div>

            <div className="glass-dark p-8 rounded-3xl text-white">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-burnt-orange/30 rounded-2xl flex items-center justify-center">
                  <Clock className="w-8 h-8 text-burnt-orange" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">&lt; 3 phút</h3>
                  <p className="text-white/80">Hàng xóm đến cửa</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-white/80">Xác nhận SOS</span>
                  <span className="font-bold text-burnt-orange">&lt; 30 giây</span>
                </div>
                <div className="w-full bg-white/20 rounded-full h-2">
                  <div className="bg-burnt-orange h-2 rounded-full w-4/5"></div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 glass-primary p-8 rounded-3xl text-center">
            <h3 className="text-2xl font-bold text-midnight-blue mb-4 flex items-center justify-center gap-3">
              <Heart className="w-8 h-8 text-burnt-orange" />
              Kết Quả An Toàn
            </h3>
            <p className="text-lg text-midnight-blue/80 mb-6">
              Số mạng sống cứu được và sự kiện nghiêm trọng được giảm thiểu
            </p>
            <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-electric-blue to-burnt-orange">
              trên mỗi 10.000 hộ gia đình
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="px-4 py-16">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-midnight-blue">
            Cùng Xây Dựng Tương Lai An Toàn
          </h2>
          <p className="text-lg text-midnight-blue/80 mb-12 max-w-2xl mx-auto">
            Hãy là một phần của hành trình biến đổi cách chúng ta chăm sóc và bảo vệ gia đình trong kỷ nguyên số.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-8">
            <button className="btn-primary group px-8 py-4">
              <div className="flex items-center gap-3">
                <Heart className="w-5 h-5" />
                <span>Tham Gia Cộng Đồng</span>
                <ArrowRight className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </button>
            <button className="btn-secondary px-8 py-4">
              <div className="flex items-center gap-3">
                <Star className="w-5 h-5" />
                <span>Trở Thành Đối Tác</span>
              </div>
            </button>
          </div>

          <div className="text-sm text-midnight-blue/60">
            <p className="mb-2">🏠 100+ chung cư đã triển khai • 🏫 50+ trường học tin dùng • 👨‍👩‍👧‍👦 10,000+ gia đình an tâm</p>
          </div>
        </div>
      </section>

      {/* CSS for animations - moved to index.css */}
    </div>
  );
};

export default VisionMissionPage;
